import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";

// Types for Brand Pulse API responses
export type BrandSelectionResponse = {
    ok: boolean;
    data?: {
        suggested_brands?: Array<{
            name: string;
            domains: string[];
            brandTraffic: string;
            region: string;
        }>;
        tracked_brands?: Array<{
            name: string;
            dbId: number;  // Database ID for deletion
            domains: string[];
            brandTraffic: string;
            region: string;
        }>;
    };
    error?: string;
};

// Track brand response
export type TrackBrandResponse = {
    ok: boolean;
    data?: {
        id: number;
        brandName: string;
        brandDomain: string;
        country: string;
    };
    error?: string;
};

// Brand Pulse API
const brandPulse = createApi({
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL }),
    reducerPath: "api.brandPulse",
    tagTypes: ["brandPulse"],
    endpoints: (builder) => ({

        // Get brand selection (suggested + tracked brands)
        getBrandSelection: builder.query<BrandSelectionResponse, {
            workspaceId: number;
            selectedCountry?: string;
        }>({
            query: ({ workspaceId, selectedCountry }) => {
                const params = new URLSearchParams();
                params.append("workspaceId", String(workspaceId));
                if (selectedCountry) {
                    params.append("selectedCountry", selectedCountry);
                }
                return {
                    url: `/brand-pulse/getBrandSelection?${params.toString()}`,
                    method: "GET",
                    headers: {
                        "Cache-Control": "no-cache",
                        Accept: "application/json",
                    },
                };
            },
            providesTags: ["brandPulse"],
        }),

        // Track a brand by its name and country
        trackBrand: builder.mutation<TrackBrandResponse, {
            workspaceId: number;
            brandName: string;
            country: string;
            brandDomain?: string;
        }>({
            query: ({ workspaceId, brandName, country, brandDomain }) => ({
                url: `/brand-pulse/trackBrand`,
                method: "POST",
                body: JSON.stringify({ workspaceId, brandName, country, brandDomain }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
            invalidatesTags: ["brandPulse"],
        }),

        // Untrack a brand by its database ID
        untrackBrand: builder.mutation<{ ok: boolean; error?: string }, number>({
            query: (dbId) => ({
                url: `/brand-pulse/trackBrand/${dbId}`,
                method: "DELETE",
                headers: {
                    Accept: "application/json",
                },
            }),
            invalidatesTags: ["brandPulse"],
        }),
        getDomains: builder.query<{ domains: string[] }, { workspace_id: string, country: any }>({
            query: ({ workspace_id, country }) => ({
                url: `/brand-pulse/domains`,
                method: "POST",
                body: JSON.stringify({ workspace_id: workspace_id, country: country }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
            providesTags: ["brandPulse"],
        }),
        getIndustries: builder.query<{ domains: string[] }, { workspace_id: string, country: any }>({
            query: ({ workspace_id, country }) => ({
                url: `/brand-pulse/industries`,
                method: "POST",
                body: JSON.stringify({ workspace_id: workspace_id, country: country }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
            providesTags: ["brandPulse"],
        }),
        getBrands: builder.query<
            any,
            { workspace_id: string; country: string; industries?: string[]; domains?: string[] }
        >({
            query: ({ workspace_id, country, industries = [], domains = [] }) => ({
                url: `/brand-pulse/brands`,
                method: "POST",
                body: JSON.stringify({ workspace_id, country, industries, domains }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
            providesTags: ["brandPulse"],
        }),

    }),
});

export const {
    useGetBrandSelectionQuery,
    useTrackBrandMutation,
    useUntrackBrandMutation,
    useGetDomainsQuery,
    useGetIndustriesQuery,
    useGetBrandsQuery
} = brandPulse;

export default brandPulse;


